import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Image, Film, FileText, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

/**
 * Multi-file upload component with drag & drop and compression
 */
export const FileUploader = ({
  accept = 'image/*,video/*',
  maxFiles = 10,
  maxSizeMB = 50,
  onFilesChange,
  className,
  label = 'Arrastra archivos aquí o haz clic para seleccionar',
  compressImages = true
}) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const compressImage = async (file) => {
    if (!compressImages || !file.type.startsWith('image/')) {
      return file;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Max dimensions
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;
          
          let { width, height } = img;
          
          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.8
          );
        };
      };
    });
  };

  const processFiles = async (fileList) => {
    setError(null);
    
    const newFiles = Array.from(fileList);
    
    // Check max files
    if (files.length + newFiles.length > maxFiles) {
      setError(`Máximo ${maxFiles} archivos permitidos`);
      return;
    }
    
    // Check file sizes
    for (const file of newFiles) {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`El archivo ${file.name} excede ${maxSizeMB}MB`);
        return;
      }
    }
    
    setUploading(true);
    
    try {
      const processedFiles = await Promise.all(
        newFiles.map(async (file) => {
          const processed = await compressImage(file);
          const preview = URL.createObjectURL(processed);
          return {
            id: Math.random().toString(36).substr(2, 9),
            file: processed,
            name: file.name,
            size: processed.size,
            type: processed.type,
            preview
          };
        })
      );
      
      const updatedFiles = [...files, ...processedFiles];
      setFiles(updatedFiles);
      onFilesChange?.(updatedFiles.map(f => f.file));
    } catch (err) {
      setError('Error al procesar los archivos');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  }, [files]);

  const handleChange = (e) => {
    if (e.target.files) {
      processFiles(e.target.files);
    }
  };

  const removeFile = (id) => {
    const file = files.find(f => f.id === id);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    const updatedFiles = files.filter(f => f.id !== id);
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles.map(f => f.file));
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('video/')) return Film;
    return FileText;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Drop zone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
          isDragging
            ? 'border-[#0047FF] bg-[#0047FF]/5'
            : 'border-[#E5E7EB] dark:border-gray-600 hover:border-[#0047FF]/50',
          uploading && 'pointer-events-none opacity-50'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={label}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleChange}
          className="hidden"
          aria-hidden="true"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-10 h-10 text-[#0047FF] animate-spin" />
            <p className="text-sm text-[#52525B]">Procesando archivos...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className={cn(
              'w-10 h-10 transition-colors',
              isDragging ? 'text-[#0047FF]' : 'text-[#A1A1AA]'
            )} />
            <p className="text-sm text-[#52525B] dark:text-gray-400">{label}</p>
            <p className="text-xs text-[#A1A1AA]">
              Máximo {maxFiles} archivos, {maxSizeMB}MB cada uno
            </p>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-500 mt-2"
        >
          {error}
        </motion.p>
      )}

      {/* File previews */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
          >
            {files.map((file) => {
              const Icon = getFileIcon(file.type);
              return (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative group"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F7] dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700">
                    {file.type.startsWith('image/') ? (
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : file.type.startsWith('video/') ? (
                      <video
                        src={file.preview}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-10 h-10 text-[#A1A1AA]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.id);
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={`Eliminar ${file.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  {/* File info */}
                  <p className="text-xs text-[#52525B] dark:text-gray-400 mt-1 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-[#A1A1AA]">{formatSize(file.size)}</p>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FileUploader;
